import type { Breadcrumb, ErrorEvent } from '@sentry/react'
import Routes from './utils/routes'

// Account/customer/client/user/wallet identifiers attached to a key. Anchored
// on the field name so that timestamps, build numbers, port numbers, trace IDs
// and other diagnostic 6+ digit runs stay readable in Sentry. Matches forms
// like `accountId: 12345678`, `"customerNo":"00099"`, `user_id=42`, `wallet
// number = 87654321`. The previous unanchored `\b\d{6,}\b` pattern destroyed
// signal needed for triage (every release tag, every timestamp).
const ID_KEY_DIGIT_PATTERN =
    /\b(?:account|customer|client|user|wallet)[-_ ]?(?:id|no|number)\b["']?\s*[:=]\s*["']?\d+/gi

// Fallback for bare 7+ digit runs that appear without a keyword prefix —
// free-form error strings like "withdrawal failed for 12345678" or URL
// fragments like "/accounts/12345678/balance". The 7-digit floor keeps
// years (4 digits) and TCP ports (≤5 digits) readable; ThinkTrader account
// numbers are well above this threshold.
const BARE_LONG_DIGITS = /\b\d{7,}\b/g

const piiPatterns = [
    /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,    // email
    ID_KEY_DIGIT_PATTERN,                                  // <field>=<digits> identifiers
    /(\+?\d[\d\s\-()]{7,}\d)/g,                           // phone numbers
    BARE_LONG_DIGITS,                                      // bare 7+ digit runs (account-number fallback)
]

export const scrubString = (s: string): string =>
    piiPatterns.reduce((str, re) => str.replace(re, '[Filtered]'), s)

export const beforeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
    // Drop console breadcrumbs entirely — they capture verbatim
    // log output that frequently contains user/error text we don't
    // want sent to Sentry.
    if (breadcrumb.category === 'console') return null
    // Scrub PII from any remaining breadcrumb message / URL.
    if (breadcrumb.message)
        breadcrumb.message = scrubString(breadcrumb.message)
    if (breadcrumb.data && typeof breadcrumb.data.url === 'string')
        breadcrumb.data.url = scrubString(breadcrumb.data.url)
    return breadcrumb
}

// ---------------------------------------------------------------------------
// Noise filtering
//
// Every entry below is a "drop this event" rule. Rather than one hand-written
// `if (event.exception?.values?.some(...)) return null` block per rule (which
// is what made this file unwieldy), each rule is a declarative NoiseRule and a
// single evaluator (`ruleMatches`) walks the event once. Adding a new filter is
// now one appended object plus its rationale comment — no new control flow.
//
// A rule fires when, after the optional `noPortalFrame` event-level gate:
//   - `custom` returns true (escape hatch for bespoke logic), OR
//   - `message` matches `event.message`, OR
//   - SOME exception value satisfies every exception-level field that is set
//     (`type`, `value`, `fn`, `filename`). Those fields are ANDed *within one
//     exception*, which preserves the original same-exception coupling (e.g.
//     "value X with a frame whose function is checkPerfReady").
//
// `noPortalFrame` is the authoritative "no first-party code on the stack" gate:
// a genuine first-party error always carries a `/static/js/` frame, so gating on
// its absence keeps real bugs reportable even when the message/value looks like
// a known third-party string. Localisation note: Error.message / DOMException
// .message are emitted in English by V8 / JavaScriptCore / SpiderMonkey
// regardless of browser UI locale, so these string patterns are stable across
// users; the real drift risk is browser *version* updates tweaking wording, so
// prefer `type` matches where the exception type is unique.
// ---------------------------------------------------------------------------

type SentryException = NonNullable<NonNullable<ErrorEvent['exception']>['values']>[number]

type NoiseRule = {
    id: string                              // PORTAL-20 id(s) for triage cross-reference
    note: string                            // one-line "why this is not actionable"
    noPortalFrame?: boolean                 // require absence of any /static/js/ frame
    message?: RegExp                        // tested against event.message
    type?: string                           // require exception.type to equal this
    value?: RegExp                          // tested against exception.value
    fn?: RegExp                             // some frame.function in that exception
    filename?: RegExp                       // some frame.filename in that exception
    custom?: (event: ErrorEvent) => boolean // bespoke predicate (combined with noPortalFrame)
}

const RANGE_ERROR_VALUE = /Maximum call stack size exceeded/
const JSON_PARSE_VALUE = /is not valid JSON/
const UNEXPECTED_IDENTIFIER_VALUE = /Unexpected identifier/
const UNEXPECTED_TOKEN_WORD_VALUE = /^Unexpected token '\w+'$/
const TRANSLATE_DISABLED_VALUE = 'translateDisabled'
const SET_CONTACT_AUTOFILL_FN = /setContactAutofillValuesFromBridge/
const READONLY_PROPERTY_VALUE = /Attempted to assign to readonly property/
const PERFECT_SCROLLBAR_TARGET = /ps__(?:thumb|rail)-[xy]|scrollbar-container/
const BRAINTREE_VALUE = /_savedBodyProperties/
const DOM_INTERACTIVE_VALUE = /reading 'domInteractive'/
const CHECK_PERF_READY_FN = /checkPerfReady/
// endsWith('autoFillCallback' | 'insertInput') or exactly 'execute_auto_fill'
const AUTOFILL_BRIDGE_FN = /(?:autoFillCallback|insertInput)$|^execute_auto_fill$/
const BLOCKED_FRAME_VALUE = /Blocked a frame with origin/
const HTML_DOCUMENT_INJECTED_FN = /^HTMLDocument\.\w{1,3}$/
const CHROME_EXTENSION_FILENAME = /^chrome-extension:\/\//
const ADD_LISTENER_VALUE = /reading 'addListener'/
const BLOB_SCRIPT_FILENAME = /^blob:/
const CRAWLER_INJECTED_FN = /^(?:ClickSimulator|startLinkCollector|runCollectionFlow|scanAllElements|scanDocument|getSelectorPath)\b/
const BODY_TOUCHED_VALUE = /properties of undefined \(setting 'bodyTouched'\)/
const APM_RUM_FETCH_PATCH_FILENAME = /@elastic\/apm-rum-core\/.*\/fetch-patch\.js/
const APM_RUM_FETCH_PATCH_VALUE = /did not match the expected pattern/
const REMOVE_CHILD_NOT_A_CHILD_VALUE = /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node/
// Eval'd / injected inline scripts (in-app WebView host shims, browser
// extensions, GTM-injected snippets) execute with no sourceURL, so V8 /
// JavaScriptCore report every frame as `<anonymous>:line:col`. Sentry parses
// that into a frame whose filename is exactly "<anonymous>".
const ANONYMOUS_FILENAME = /^<anonymous>$/
// Mechanism Sentry's browserApiErrors integration stamps on an error thrown
// synchronously inside an addEventListener callback. A messageless minified
// vendor class thrown here (e.g. "ze: No error message") is an injected
// analytics/marketing/WebView-host listener, not portal code.
const ADD_EVENT_LISTENER_MECHANISM = 'auto.browser.browserapierrors.addEventListener'
// Externally hosted vendor bundles whose filename is visible directly in the
// frame (Zendesk CDN, Cloudflare RUM, Trading Central, LinkedIn Insight Tag).
// `includes`-style substrings plus the hashed Zendesk-classic `/modules.<hash>.js`
// chunk. Uses substring (not anchored) matching because beforeSend receives
// absolute URLs (https://portal…/Widgets/lib/…); the Sentry UI only normalises to
// a relative path for display, so anchored checks miss in production
// (PORTAL-20-70 leaked). The LinkedIn Insight Tag marketing pixel is injected via
// the tag manager and served same-origin proxied under /li.lms-analytics/
// (insight.old.min.js); it crashes with a SyntaxError in niche WebKit browsers
// (Baidu Explorer) and is surfaced only because Elastic APM RUM patches
// window.fetch — never portal code (PORTAL-20-M).
const VENDOR_FILENAME = /\/ekr\/|\/web_widget\/|\/beacon\.min\.js(?:$|\?|\/)|\/Widgets\/lib\/|\/modules\.[a-f0-9]{8,}\.js$|\/li\.lms-analytics\//
// Native Chromium DecompressionStream (zlib inflate) decode failures forwarded
// by a third-party analytics/ads/RUM tag via onunhandledrejection; the event is
// frameless (stacktrace null). The four variants below are the browser-native
// message texts; portal bundles no decompression code (no pako/zlib/fflate/
// DecompressionStream), so these strings never collide with first-party errors
// (PORTAL-20-8H / 8F / 8G / 8J / THPRT-6314).
const DECOMPRESSION_STREAM_VALUE = /The compressed data was not valid|Compressed input was truncated|invalid distance too far back|invalid stored block lengths/
// Checkout.com Flow SDK device-fingerprint init timeout: the SDK's own Risk.js
// fingerprint service scheduling its own setTimeout reject, thrown from its CDN
// bundle with no first-party frame (PORTAL-20-90 / THPRT-6352). Value-scoped so
// only this one message is dropped; the render/reconcile TypeError (THPRT-6380)
// and the Apple-Pay-in-iOS-WebView InvalidAccessError (THPRT-6316) on the same
// bundle stay visible as real payment-path signals. Vendor-owned message,
// absent from portal source.
const CHECKOUT_FINGERPRINT_VALUE = /Fingerprint service initialization timeout/
// The big message/value alternation. Every token here is a pure string match
// with NO frame gating — third-party globals, transient network noise, and
// in-app-browser/webview bridge shims whose strings never collide with our code.
// See the per-token notes inline. ("Blocked a frame with origin" is route-scoped
// in its own rule, not here, so portal-owned cross-origin iframes — Checkout.com
// Frames, SumSub, GreenID, TrustDock — stay reportable. "reading 'domInteractive'"
// is function-scoped on checkPerfReady, not here, so first-party domInteractive
// reads stay reportable.)
//   ResizeObserver loop — browser timing quirk, not a bug
//   ChunkLoadError / "Loading (CSS )?chunk N failed" — transient, usually a deploy while a tab was open
//   Failed to fetch dynamically imported module — same as above
//   Network request/NetworkError/Load failed/Failed to fetch/cancelled — offline or navigated away.
//     Sentry's fetch instrumentation appends " (host)" to the value, so patterns allow an optional trailing "(host)".
//   fetchOk attempt timed out — first-party redux/sagas.ts fetchOk$ delay() race; same offline/slow-network root cause; anchored to the exact phrase.
//   Non-Error promise rejection — Sentry's stringification of non-Error throws; no stack
//   Object captured as promise rejection with keys: [object has no keys] — unhandled reject()/reject({}) with no payload (PORTAL-20-4Z)
//   The operation is insecure — Firefox in private mode / Tracking Protection denying storage (PORTAL-20-57, 5Q)
//   zaloJSV2 — GTM-injected Zalo marketing tag referenced before load; marketing owns GTM
//   google — window.google noise when pay.google.com is blocked (ad blocker / CSP / region); not library-specific
//   postMessage null / "*.postMessage is not a function" / webkit.messageHandlers / Java object is gone /
//     "Error invoking (post|postEvent|batch): Method not found" — iOS WKMessageHandlers + Android JS-interface
//     + IAB SDK shims. Bare "Method not found" deliberately NOT filtered (would drop first-party Error('Method not found')).
//   window.android.* is not a function — Android WebView host shim (PORTAL-20-5C)
//   runtime.sendMessage — Chrome extension messaging
//   LIDNotifyId is not defined — LinkedIn Insight Tag firing before its loader; marketing owns the pixel
//   userScripts is not defined — Firefox WebExtensions global referenced by userscript managers (PORTAL-20-7N)
//   swbrowser is not defined — Android Chrome WebView content-script injected global (PORTAL-20-7X)
//   SCDynimacBridge — Snapchat in-app browser injected native-bridge probe; HTML "global code" frame (PORTAL-20-1H / 73)
//   UCShellJava — UC Browser injected host shim calling UCShellJava.sdkEventFire (PORTAL-20-75)
//   new <minified>.InboundFilters — Zendesk Web Widget initialising its own embedded Sentry SDK (PORTAL-20-18)
//   Unexpected token '<' / Unexpected EOF / end of script / end of input / missing ) — JSON.parse fed an HTML
//     response (CDN/deploy race serving index.html where JSON was expected); transient
//   ifameElement.contentDocument — third-party iframe shim typo'd global
//   TradingViewApi is not defined — ThinkTrader native WebView posts events for a TradingView script not loaded in portal (PORTAL-20-32)
const NOISY = /ResizeObserver loop|ChunkLoadError|Loading (?:CSS )?chunk \d+ failed|Failed to fetch dynamically imported module|NetworkError when attempting|Network request failed|^fetchOk attempt timed out$|^Load failed(?: \([^)]*\))?$|^Failed to fetch(?: \([^)]*\))?$|^cancelled$|Non-Error promise rejection|^Object captured as promise rejection with keys: \[object has no keys\]$|^The operation is insecure\.?$|zaloJSV2|google is not defined|Can't find variable: google\b|reading 'postMessage'|evaluating '[^']*\.postMessage'|\.postMessage is not a function|webkit\.messageHandlers|Java object is gone|Error invoking (?:post|postEvent|batch): Method not found|window\.android\.\w+ is not a function|runtime\.sendMessage|LIDNotifyId is not defined|userScripts is not defined|swbrowser is not defined|SCDynimacBridge|UCShellJava|new \w+\.InboundFilters|Unexpected token '<'|^Unexpected EOF$|^Unexpected end of script$|^Unexpected end of input$|^missing \) after argument list$|ifameElement\.contentDocument|TradingViewApi is not defined/i

const NOISE_RULES: NoiseRule[] = [
    {
        id: 'NOT_AUTHORIZED',
        note: 'Expected expired-session / revoked-token rejection, not an actionable error',
        message: /NOT_AUTHORIZED/,
        value: /NOT_AUTHORIZED/,
    },
    {
        id: '17',
        note: 'Third-party recursion (GTM/hCaptcha/Google Translate/perf-RUM/iOS-Chrome autofill) outside the React tree; iOS Chrome wraps the RangeError in a plain Error so match the value, gated on no portal frame',
        noPortalFrame: true,
        value: RANGE_ERROR_VALUE,
    },
    {
        id: '7G',
        note: 'Native/in-app WebView hosts post non-JSON into our window; their injected handler JSON.parse throws SyntaxError. First-party keycloakAuth wraps response.json() with a distinct message and carries a portal frame, so it stays reportable',
        noPortalFrame: true,
        type: 'SyntaxError',
        value: JSON_PARSE_VALUE,
    },
    {
        id: '8Z / 8Y',
        note: 'Injected/proxied inline script (ISP HTTP-to-HTTPS rewrite snippet, browser extension, in-app WebView shim) parses a bare "https" URL token as JavaScript in the HTML document context and throws "SyntaxError: Unexpected identifier \'https\'" with the only frame attributed to the document URL (no /static/js/ frame). Seen on /create-account (8Z) and /account/social-registration (8Y). A genuine first-party SyntaxError fails the build for every user and carries a /static/js/ frame, so it stays reportable',
        noPortalFrame: true,
        type: 'SyntaxError',
        value: UNEXPECTED_IDENTIFIER_VALUE,
    },
    {
        id: '8X',
        note: 'Injected/proxied inline script (ISP rewrite snippet, browser extension, in-app WebView shim) executes in the HTML document context and the engine parses an injected fragment as JavaScript, throwing a word-token SyntaxError e.g. "Unexpected token \'else\'" on /account/social-registration (no /static/js/ frame). Anchored to the single-word-token form so it never overlaps "Unexpected token \'<\'" (JSON.parse of HTML, handled in NOISY); a genuine first-party SyntaxError fails the build for every user and carries a /static/js/ frame, so it stays reportable',
        noPortalFrame: true,
        type: 'SyntaxError',
        value: UNEXPECTED_TOKEN_WORD_VALUE,
    },
    {
        id: '7F',
        note: 'iOS Live Translate raises bare-string "translateDisabled" via onunhandledrejection (message-only, no values) or as an exception value; first-party Error("translateDisabled") carries a portal frame and stays reportable',
        noPortalFrame: true,
        custom: (event) =>
            (event.message === TRANSLATE_DISABLED_VALUE && !event.exception?.values?.length)
            || (event.exception?.values?.some(ex => ex.value === TRANSLATE_DISABLED_VALUE) ?? false),
    },
    {
        id: 'FB-IAB',
        note: 'Facebook in-app browser injects setContactAutofillValuesFromBridge on an <anonymous> frame; crashes iterating form elements',
        fn: SET_CONTACT_AUTOFILL_FN,
    },
    {
        id: '3N',
        note: 'perfect-scrollbar (vendor) assigns e.pageX/Y on a TouchEvent; iOS Safari 18+ throws readonly-property. Fingerprint = readonly value + the captured DOM target (extra.arguments[0].currentTarget) being a perfect-scrollbar selector. Bundle URL defeats filename/function scoping',
        custom: (event) => {
            if (!event.exception?.values?.some(ex => READONLY_PROPERTY_VALUE.test(ex.value ?? '')))
                return false
            const extraArgs = (event.extra as { arguments?: unknown } | undefined)?.arguments
            const firstArg = Array.isArray(extraArgs) ? extraArgs[0] : undefined
            const currentTarget = (firstArg as { currentTarget?: unknown } | undefined)?.currentTarget
            return typeof currentTarget === 'string' && PERFECT_SCROLLBAR_TARGET.test(currentTarget)
        },
    },
    {
        id: '6R',
        note: 'Braintree Web SDK Modal teardown race reads this._savedBodyProperties after it was wiped (re-entrant close on iOS WKWebView). Property name survives terser (no property mangling) and is unique to Braintree internals',
        value: BRAINTREE_VALUE,
    },
    {
        id: '2E',
        note: 'GTM-injected perf-RUM SDK reads performance.timing.domInteractive when timing is undefined; signature is the checkPerfReady function on the eval-injected frame. Function-scoped so first-party domInteractive reads stay reportable',
        value: DOM_INTERACTIVE_VALUE,
        fn: CHECK_PERF_READY_FN,
    },
    {
        id: '4W / 6A',
        note: 'TikTok IAB autofill bridge (autoFillCallback / insertInput) and Android Chrome password-manager extension (execute_auto_fill) on <anonymous> frames dispatch synthetic events into first-party handlers',
        fn: AUTOFILL_BRIDGE_FN,
    },
    {
        id: '5P',
        note: '"Blocked a frame with origin" SecurityError from third-party iframes/inline scripts/extensions. Dropped on pre-auth routes (portal owns no cross-origin iframes there), for HTML inline <script> ("global code" frames in a non-.js file), or when the stack carries no first-party /static/js/ frame at all. The no-frame fallback (PORTAL-20-47) catches the leak when event.transaction was not populated by the routing instrumentation — the SecurityError fires from third-party iframe/extension code outside our React render, before the route transaction is established, so the Sentry culprit shows /account/login while event.transaction is absent. Real SecurityErrors from our payment/KYC iframes carry /static/js/ frames and stay reportable',
        custom: (event) => {
            if (!event.exception?.values?.some(ex => BLOCKED_FRAME_VALUE.test(ex.value ?? '')))
                return false
            const isPreAuthRoute = event.transaction === Routes.account.login
                || event.transaction === Routes.account.check2fa
                || (typeof event.transaction === 'string' && event.transaction.startsWith(Routes.register.root))
            const isHtmlInlineScript = event.exception?.values?.some(ex => {
                const frames = ex.stacktrace?.frames
                if (!frames || frames.length === 0) return false
                return frames.every(f =>
                    f.function === 'global code'
                    && typeof f.filename === 'string'
                    && !f.filename.endsWith('.js'))
            }) ?? false
            const hasNoPortalBundleFrame = !event.exception?.values?.some(ex =>
                ex.stacktrace?.frames?.some(f =>
                    typeof f.filename === 'string' && f.filename.includes('/static/js/')))
            return isPreAuthRoute || isHtmlInlineScript || hasNoPortalBundleFrame
        },
    },
    {
        id: '27 / 5H',
        note: 'Miui (Xiaomi) / Vivo browsers inject autofill handlers on HTMLDocument with single-char minified property names ("HTMLDocument.c"); portal never extends HTMLDocument that way',
        fn: HTML_DOCUMENT_INJECTED_FN,
    },
    {
        id: '7R',
        note: 'Crypto-wallet extensions (MetaMask) throw EIP-1193 disconnect via unhandledrejection; every frame is chrome-extension://. Gated on no portal frame so first-party rejections interacting with an extension stay reportable',
        noPortalFrame: true,
        filename: CHROME_EXTENSION_FILENAME,
    },
    {
        id: '7Y',
        note: 'Extension/userscript content scripts injected as a Blob worker reference chrome.runtime/browser.runtime (undefined in the blob) and crash reading addListener; every frame is a blob: URL. Gated so a first-party MediaQueryList addListener read stays reportable',
        noPortalFrame: true,
        value: ADD_LISTENER_VALUE,
        filename: BLOB_SCRIPT_FILENAME,
    },
    {
        id: '7Q',
        note: 'Bot / link-collector / accessibility-scanner scripts inject classes (ClickSimulator, startLinkCollector, scanAllElements, ...) that crash introspecting selectors; none of these identifiers exist in the bundle',
        fn: CRAWLER_INJECTED_FN,
    },
    {
        id: '7P',
        note: 'Quark browser IAB injects an obfuscated touch shim that crashes setting bodyTouched on a missing target; no first-party property is called bodyTouched',
        value: BODY_TOUCHED_VALUE,
    },
    {
        id: 'M',
        note: '@elastic/apm-rum-core fetch-patch re-throws JSON.parse failures on WebKit as "did not match the expected pattern"; throw site is always inside fetch-patch.js, never imported by portal',
        value: APM_RUM_FETCH_PATCH_VALUE,
        filename: APM_RUM_FETCH_PATCH_FILENAME,
    },
    {
        id: '2Z',
        note: 'React 17 removeChild reconciler race: external DOM mutation (page translation, ad blockers, IAB overlays) removes a node React still owns. Stack is fully inside react-dom/scheduler min bundles; a first-party flawed update carries a portal frame and stays reportable',
        noPortalFrame: true,
        value: REMOVE_CHILD_NOT_A_CHILD_VALUE,
    },
    {
        id: '81 / 83',
        note: 'Injected inline/eval scripts (in-app WebView shims, extensions, GTM snippets) crash with every frame an <anonymous> frame and no portal bundle frame — e.g. "isReCreate is not defined" (83, Android WebView), "reading \'hasResponse\'" (81). The referenced identifiers exist nowhere in the bundle. Gated on no portal frame, and requires a non-empty all-<anonymous> stack, so a genuine first-party error (always carrying a /static/js/ frame) stays reportable',
        noPortalFrame: true,
        custom: (event) => event.exception?.values?.some(ex => {
            const frames = ex.stacktrace?.frames
            if (!frames || frames.length === 0) return false
            return frames.every(f =>
                typeof f.filename === 'string' && ANONYMOUS_FILENAME.test(f.filename))
        }) ?? false,
    },
    {
        id: '92',
        note: 'Injected/proxied inline script whose frames some mobile browsers (Chrome iOS) attribute to the served HTML document URL (e.g. /account/individual) instead of <anonymous>, so neither the all-<anonymous> gate (81/83) nor the "global code" gate (5P) fires; e.g. a minified "Error: Aa" surfaced via onunhandledrejection. Gated on no portal frame plus a non-empty stack whose every frame is a non-.js (inline-document) frame, so a genuine first-party error (always carrying a /static/js/ frame) stays reportable',
        noPortalFrame: true,
        custom: (event) => event.exception?.values?.some(ex => {
            const frames = ex.stacktrace?.frames
            if (!frames || frames.length === 0) return false
            return frames.every(f =>
                typeof f.filename === 'string' && !f.filename.endsWith('.js'))
        }) ?? false,
    },
    {
        id: '62',
        note: 'Messageless minified vendor class (e.g. "ze: No error message") thrown synchronously inside a third-party/injected addEventListener handler (analytics/GTM/WebView-host); Sentry stamps mechanism auto.browser.browserapierrors.addEventListener with handled=no and no message. Gated on no portal frame plus an empty value, so a first-party error thrown in one of our own listeners (carries a /static/js/ frame and usually a message) stays reportable',
        noPortalFrame: true,
        custom: (event) => event.exception?.values?.some(ex =>
            ex.mechanism?.type === ADD_EVENT_LISTENER_MECHANISM
            && ex.mechanism?.handled === false
            && !ex.value) ?? false,
    },
    {
        id: 'NOISY',
        note: 'Pure message/value string noise — third-party globals, transient network, webview-bridge shims. See the NOISY token notes above',
        message: NOISY,
        value: NOISY,
    },
    {
        id: '18 / 70 / M',
        note: 'Externally hosted vendor bundles (Zendesk /ekr/ + /web_widget/ + /modules.<hash>.js, Cloudflare /beacon.min.js/, Acuity/Trading Central /Widgets/lib/, LinkedIn Insight Tag /li.lms-analytics/) whose crashes are not actionable from portal; never imported so first-party regressions never share the stack',
        filename: VENDOR_FILENAME,
    },
    {
        id: '8H / 8F / 8G / 8J',
        note: 'Native Chromium DecompressionStream (zlib inflate) decode failures forwarded by a third-party analytics/ads/RUM tag via onunhandledrejection; frameless (THPRT-6314). Portal bundles no decompression code, so these native message strings never collide with first-party errors',
        noPortalFrame: true,
        value: DECOMPRESSION_STREAM_VALUE,
    },
    {
        id: '90',
        note: 'Checkout.com Flow SDK device-fingerprint init timeout (90 / THPRT-6352): the SDK\'s own setTimeout reject, all frames on the checkout-web-components.checkout.com bundle with no first-party frame. Value-scoped to this one message only; the render/reconcile TypeError (THPRT-6380) and the Apple-Pay-in-iOS-WebView InvalidAccessError (THPRT-6316) on the same bundle stay visible. Gated on no portal frame so a genuine first-party payment error (CheckoutFlow.tsx catch carries a /static/js/ frame) stays reportable',
        noPortalFrame: true,
        value: CHECKOUT_FINGERPRINT_VALUE,
    },
]

const someFrame = (
    ex: SentryException,
    pred: (f: NonNullable<NonNullable<SentryException['stacktrace']>['frames']>[number]) => boolean,
): boolean => ex.stacktrace?.frames?.some(pred) ?? false

// A rule's exception-level fields (type/value/fn/filename) are ANDed within a
// single exception. A rule with none of those set never matches via this path
// (it must rely on `message` or `custom`).
const exceptionMatches = (rule: NoiseRule, ex: SentryException): boolean => {
    if (rule.type === undefined && rule.value === undefined
        && rule.fn === undefined && rule.filename === undefined)
        return false
    if (rule.type !== undefined && ex.type !== rule.type) return false
    if (rule.value && !rule.value.test(ex.value ?? '')) return false
    const fnRe = rule.fn
    if (fnRe && !someFrame(ex, f => typeof f.function === 'string' && fnRe.test(f.function)))
        return false
    const fileRe = rule.filename
    if (fileRe && !someFrame(ex, f => typeof f.filename === 'string' && fileRe.test(f.filename)))
        return false
    return true
}

const ruleMatches = (rule: NoiseRule, event: ErrorEvent, noPortalBundleFrame: boolean): boolean => {
    if (rule.noPortalFrame && !noPortalBundleFrame) return false
    if (rule.custom) return rule.custom(event)
    if (rule.message && rule.message.test(event.message ?? '')) return true
    return event.exception?.values?.some(ex => exceptionMatches(rule, ex)) ?? false
}

export const beforeSend = (event: ErrorEvent): ErrorEvent | null => {
    // Authoritative "no first-party code on the stack" signal: a genuine
    // first-party error always carries a /static/js/ frame. Computed once and
    // reused by every noPortalFrame-gated rule.
    const noPortalBundleFrame = !event.exception?.values?.some(ex =>
        ex.stacktrace?.frames?.some(f =>
            typeof f.filename === 'string' && f.filename.includes('/static/js/')))

    if (NOISE_RULES.some(rule => ruleMatches(rule, event, noPortalBundleFrame)))
        return null

    if (event.message)
        event.message = scrubString(event.message)
    if (event.exception?.values) {
        for (const ex of event.exception.values) {
            if (ex.value)
                ex.value = scrubString(ex.value)
        }
    }
    return event
}
